/**
 * @module providers/IdentityProvider
 * @description React Context Provider for Netlify Identity authentication.
 * Manages user authentication state, JWT token generation, and provides identity headers for API calls.
 */

import React, { useContext } from 'react';
import { useNetlifyIdentity } from 'react-netlify-identity';

import { get, isLocalStorageAvailable } from 'src/modules/utils';
import { nowEpoch } from 'src/utils';

/**
 * @interface IdentityContextValue
 * @description Type for the identity context value from useNetlifyIdentity
 */
type IdentityContextValue = ReturnType<typeof useNetlifyIdentity>;

const IdentityContext = React.createContext<IdentityContextValue | undefined>(
  undefined,
);

/**
 * @interface IdentityProviderProps
 * @description Props for IdentityProvider component
 */
interface IdentityProviderProps {
  children: React.ReactNode;
}

/**
 * @component IdentityProvider
 * @summary React Context Provider to provide a given user's Netlify auth services and metadata
 * @param props - Component props
 * @returns React Context provider
 */
const IdentityProvider: React.FC<IdentityProviderProps> = (props) => {
  const domain = `https://${process.env.REACT_APP_NETLIFY_DOMAIN_NAME}`;
  const enableRunRoutes = false; // enableRunRoutes OFF, so invites and reset password will go through the widget instead

  const identity = useNetlifyIdentity(domain, () => {}, enableRunRoutes);

  // Extend window with identity property
  if (typeof window !== 'undefined') {
    window.identity = identity;
  }

  return (
    <IdentityContext.Provider value={identity}>
      {props.children}
    </IdentityContext.Provider>
  );
};

/**
 * @func identityHeadersPromise
 * @async
 * @summary Helper function to fetch access token (jwt) + auth headers
 *  - to authenticate netlify requests
 *  - wrap this function **around** fetches to netlify functions (see api/apiCall.js)
 *  - if fails, will reload page every 4s to retry login
 * @returns Promise with netlify headers as object
 */
export const identityHeadersPromise = (): Promise<Record<string, string>> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  let user = get(window.identity, 'user', null);
  user = user || window.netlifyIdentity?.currentUser();

  if (!user) {
    return Promise.resolve(headers);
  }

  return user
    .jwt()
    .then((token: string) => ({
      ...headers,
      Authorization: `Bearer ${token}`,
    }))
    .catch((e: Error) => {
      console.error(e);

      let timeout = 4000;

      if (isLocalStorageAvailable()) {
        try {
          const lastRetry = localStorage.getItem('lastLoginRetry');
          const now = nowEpoch();
          localStorage.setItem('lastLoginRetry', now.toString());

          if (lastRetry && now - +lastRetry > timeout) {
            timeout = 500;
          }
        } catch (err) {
          // Silently fail if localStorage operations fail
        }
      }
      console.warn(`reloading in ${timeout / 1000}s`);
      setTimeout(() => window.location.reload(), timeout);
      return headers;
    });
};

/**
 * @func useIdentity
 * @summary React Hook to hook into `IdentityContext` to get context value inside child components
 * @returns Hook into `IdentityContext`
 */
export const useIdentity = (): IdentityContextValue => {
  const context = useContext(IdentityContext);
  if (!context) {
    throw new Error('useIdentity must be used within an IdentityProvider');
  }
  return context;
};

export default IdentityProvider;
